/**
 * UBM Vendors Page - Manage vendor credentials
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Key,
  Bot,
  Calendar,
  Upload,
  Download,
  Loader2,
  ChevronLeft,
  Eye,
  EyeOff,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function UBMVendors() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [vendors, setVendors] = useState([]);
  const [masterAgents, setMasterAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [showDialog, setShowDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    vendor_name: "",
    master_agent_id: "",
    username: "",
    password: "",
    notes: ""
  });
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vendor: null });
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  
  useEffect(() => {
    loadData();
  }, [orgId]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load vendors
      const vendorsRes = await api.get(`/ubm/${orgId}/vendors`);
      setVendors(vendorsRes.data.vendors || []);
      
      // Load master agents for linking
      try {
        const agentsRes = await api.get(`/studio/agents`);
        setMasterAgents(agentsRes.data.agents || []);
      } catch (e) {
        // Studio might not be accessible
        setMasterAgents([]);
      }
    } catch (error) {
      console.error("Failed to load vendors:", error);
      if (error.response?.status === 403) {
        toast.error("UBM module not enabled for this organization");
        navigate(`/org/${orgId}`);
      } else {
        toast.error("Failed to load vendors");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const openCreateDialog = () => {
    setEditingVendor(null);
    setForm({
      vendor_name: "",
      master_agent_id: "",
      username: "",
      password: "",
      notes: ""
    });
    setShowPassword(false);
    setShowDialog(true);
  };
  
  const openEditDialog = (vendor) => {
    setEditingVendor(vendor);
    setForm({
      vendor_name: vendor.vendor_name || "",
      master_agent_id: vendor.master_agent_id || "",
      username: "", // Don't prefill encrypted values
      password: "",
      notes: vendor.notes || ""
    });
    setShowPassword(false);
    setShowDialog(true);
  };
  
  const handleSave = async () => {
    if (!form.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }
    
    if (!editingVendor && (!form.username || !form.password)) {
      toast.error("Username and password are required for new vendors");
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        vendor_name: form.vendor_name,
        master_agent_id: form.master_agent_id || null,
        notes: form.notes || null
      };
      
      // Only include credentials if provided
      if (form.username) payload.username = form.username;
      if (form.password) payload.password = form.password;
      
      if (editingVendor) {
        await api.put(`/ubm/${orgId}/vendors/${editingVendor.id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post(`/ubm/${orgId}/vendors`, payload);
        toast.success("Vendor created");
      }
      
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteDialog.vendor) return;
    
    try {
      await api.delete(`/ubm/${orgId}/vendors/${deleteDialog.vendor.id}`);
      toast.success("Vendor deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vendor");
    } finally {
      setDeleteDialog({ open: false, vendor: null });
    }
  };
  
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await api.post(`/ubm/${orgId}/vendors/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.warning(err));
      }
      
      loadData();
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  
  const downloadTemplate = () => {
    const headers = "vendor_name,username,password,master_agent_id,notes";
    const example = "ComEd,user@example.com,password123,agent-id-here,Optional notes";
    const csv = `${headers}\n${example}`;
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendors_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const filteredVendors = vendors.filter(v => {
    if (!searchQuery) return true;
    return v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="ubm-vendors-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/org/${orgId}`)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6 text-green-400" />
              Vendor Credentials
            </h1>
            <p className="text-zinc-400 text-sm">Manage vendor portal credentials (AES-256 encrypted)</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Bulk Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleBulkUpload}
          />
          <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>
      </div>
      
      {/* Search */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Vendors Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            {vendors.length} Vendors
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No vendors configured</p>
              <p className="text-sm">Add vendor credentials to enable bill collection</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Vendor Name</TableHead>
                  <TableHead className="text-zinc-400">Username</TableHead>
                  <TableHead className="text-zinc-400">Linked Agent</TableHead>
                  <TableHead className="text-zinc-400">Accounts</TableHead>
                  <TableHead className="text-zinc-400">Last Bill</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id} className="border-zinc-800">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-green-400" />
                        {vendor.vendor_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-zinc-800 px-2 py-1 rounded">
                        {vendor.username_masked}
                      </code>
                    </TableCell>
                    <TableCell>
                      {vendor.master_agent_id ? (
                        <Badge variant="outline" className="text-xs">
                          <Bot className="w-3 h-3 mr-1" />
                          {masterAgents.find(a => a.agent_id === vendor.master_agent_id)?.name || "Linked"}
                        </Badge>
                      ) : (
                        <span className="text-zinc-500 text-sm">Not linked</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{vendor.accounts_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-zinc-400">
                        {formatDate(vendor.last_bill_collected_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(vendor)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => setDeleteDialog({ open: true, vendor })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>
              {editingVendor ? "Edit Vendor" : "Add Vendor"}
            </DialogTitle>
            <DialogDescription>
              Credentials are encrypted with AES-256 before storage
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                placeholder="e.g., ComEd, PG&E"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>
            
            <div>
              <Label>Link to Master Agent</Label>
              <Select 
                value={form.master_agent_id} 
                onValueChange={(v) => setForm({ ...form, master_agent_id: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                  <SelectValue placeholder="Select agent (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="">None</SelectItem>
                  {masterAgents.map(agent => (
                    <SelectItem key={agent.agent_id} value={agent.agent_id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Username / Email {!editingVendor && "*"}</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder={editingVendor ? "Leave blank to keep existing" : "Portal login username"}
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>
            
            <div>
              <Label>Password {!editingVendor && "*"}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editingVendor ? "Leave blank to keep existing" : "Portal password"}
                  className="bg-zinc-800 border-zinc-700 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes about this vendor"
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingVendor ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, vendor: null })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteDialog.vendor?.vendor_name}" and its encrypted credentials.
              {deleteDialog.vendor?.accounts_count > 0 && (
                <span className="block mt-2 text-yellow-400">
                  Warning: This vendor has {deleteDialog.vendor.accounts_count} linked accounts. Delete accounts first.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
